/* global globalThis */
/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : FlowBuilder.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import "./theme.css";

import "./nodes/StartNode";
import "./nodes/QuestionNode";
import "./nodes/EndNode";

import { createNode } from "./engine/createNode";
import NodeRegistry from "./registry/NodeRegistry";
import NodePicker from "./nodes/NodePicker";
import { convertToNeuDSL } from "./engine/convertToNeuDsl";
import { validateFlow } from "./engine/validateFlow";
import ValidationConsole from "./validation/validationConsole";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../redux/actions";
import ruleEngineActions from "../../redux/actions/ruleEngineAction";
import { showToastMessage } from "../../redux/actions/actionHandler";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import { Box, Typography } from "@mui/material";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";

function getDownstreamNodes(startId, edges) {
  const visited = new Set();
  const stack = [startId];

  while (stack.length) {
    const current = stack.pop();

    for (const e of edges) {
      if (e.source === current && !visited.has(e.target)) {
        visited.add(e.target);
        stack.push(e.target);
      }
    }
  }

  return Array.from(visited);
}

const nodeTypes = Object.fromEntries(
  Object.values(NodeRegistry).map((def) => [def.type, def.renderNode]),
);

const hasExistingConnection = (edges, source, sourceHandle) =>
  edges.some((e) => e.source === source && e.sourceHandle === sourceHandle);

const isStartAlreadyConnected = (edges, sourceNodeId) =>
  sourceNodeId === "start" && edges.some((e) => e.source === sourceNodeId);

const updateNodeDataHelper = (nodes, nodeId, partialData) =>
  nodes.map((n) =>
    n.id === nodeId ? { ...n, data: { ...n.data, ...partialData } } : n,
  );

const filterDeletedNodes = (nodes, idSet) =>
  nodes.filter((n) => !idSet.has(n.id));

const filterDeletedEdges = (edges, idSet) =>
  edges.filter((e) => !idSet.has(e.source) && !idSet.has(e.target));

const removeEdgeById = (edges, edgeId) =>
  edges.filter((edge) => edge.id !== edgeId);

const enhanceSingleNode = (node, invalidNodeIds, updateNodeData, deleteNode) => {
  const isInvalid = invalidNodeIds?.includes(node.id);
  return {
    ...node,
    className: isInvalid ? "node-invalid" : "",
    data: {
      ...node.data,
      nodeId: node.id,
      updateNodeData,
      onDeleteNode: deleteNode,
      hasError: isInvalid,
    },
  };
};

export default function FlowBuilder({
  registerSave,
  ruleSetId,
  registerLoad,
  workflowId,
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [pendingConnection, setPendingConnection] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const connectingNodeRef = useRef(null);
  const { setCenter, getZoom } = useReactFlow();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [nodesToDelete, setNodesToDelete] = useState([]);

  const dispatch = useDispatch();

  const graphLoadedRef = useRef(false);

  const createNodeFromPicker = (type) => {
    if (!pendingConnection) return;

    const def = NodeRegistry[type];
    const newNodeId = `${type}-${Date.now()}`;

    setNodes((nds) =>
      nds.concat({
        id: newNodeId,
        type,
        position: pendingConnection.position,
        data: def.getDefaultData(),
      }),
    );

    setEdges((eds) =>
      eds.concat({
        id: `${pendingConnection.sourceNodeId}-${pendingConnection.sourceHandleId}-${newNodeId}`,
        source: pendingConnection.sourceNodeId,
        sourceHandle: pendingConnection.sourceHandleId,
        target: newNodeId,
        targetHandle: "in",
      }),
    );

    setPendingConnection(null);
  };

  const saveWorkflowAPI = useCallback(
    async (dsl) => {
      let response;
      try {
        const payload = workflowId
          ? {
            definitionJson: dsl,
          }
          : {
            name: `RULE_${ruleSetId}`, // or ruleSetId / ruleSetName
            definitionJson: dsl,
          };

        const api = workflowId
          ? {
            ...ruleEngineActions.update_rule_workflow,
            api_name: ruleEngineActions.update_rule_workflow.api_name.replace(
              ":id",
              workflowId,
            ),
          }
          : ruleEngineActions.create_rule_workflow;

        response = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...api,
              params: payload,
            }),
          ),
        );
      } catch (error) {
        response = {
          error: true,
          message: error,
        };
      }
      return response;
    },
    [dispatch, ruleSetId, workflowId],
  );

  const handleSelectIssue = (issue) => {
    if (issue.nodeId) {
      const node = nodes.find((n) => n.id === issue.nodeId);
      if (!node) return;

      setCenter(node.position.x + 150, node.position.y + 40, {
        zoom: Math.max(getZoom(), 1.2),
        duration: 600,
      });
    }
  };

  const updateNodeData = useCallback(
    (nodeId, partialData) => {
      setNodes((nds) => updateNodeDataHelper(nds, nodeId, partialData));
    },
    [setNodes],
  );

  useEffect(() => {
    if (!registerLoad) return;

    registerLoad((graph) => {
      if (graph?.nodes?.length) {
        graphLoadedRef.current = true;
        setNodes(graph.nodes);
        setEdges(graph.edges);
      } else {
        graphLoadedRef.current = false;
        setNodes([createNode("start", "start", { x: 150, y: 200 })]);
        setEdges([]);
      }

      setValidationResult(null);
      setConsoleOpen(false);
    });
  }, [registerLoad, setEdges, setNodes]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!graphLoadedRef.current && nodes.length === 0) {
        setNodes([createNode("start", "start", { x: 150, y: 200 })]);
      }
    }, 50);

    return () => clearTimeout(t);
  }, [nodes.length, setNodes]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Delete" && selectedEdge) {
        setEdges((eds) => removeEdgeById(eds, selectedEdge.id));
        setSelectedEdge(null);
      }
    };

    globalThis.addEventListener("keydown", onKeyDown);
    return () => globalThis.removeEventListener("keydown", onKeyDown);
  }, [selectedEdge, setEdges]);

  const onConnect = (params) => {
    setEdges((eds) => {
      const exists = hasExistingConnection(
        eds,
        params.source,
        params.sourceHandle,
      );

      if (exists) {
        showToastMessage({
          type: "error",
          message: "This handle already has a connection.",
        });
        return eds; // block
      }

      return addEdge(params, eds);
    });
  };

  const onConnectStart = (_, { nodeId, handleId }) => {
    if (!isQuestionValid(nodeId)) {
      showToastMessage({
        type: "error",
        message: "Please enter the question before creating connections.",
      });
      return;
    }
    connectingNodeRef.current = { nodeId, handleId };
  };

  const onConnectEnd = (event) => {
    const source = connectingNodeRef.current;
    if (!source) return;

    setEdges((eds) => {
      const exists = hasExistingConnection(
        eds,
        source.nodeId,
        source.handleId,
      );

      const alreadyConnected = isStartAlreadyConnected(eds, source.nodeId);
      if (exists || alreadyConnected) {
        showToastMessage({
          type: "error",
          message: "This handle already has a connection.",
        });
        connectingNodeRef.current = null;
        return eds;
      }

      const targetNode = event.target.closest(".react-flow__node");

      if (targetNode) {
        const targetId = targetNode.dataset.id;

        return eds.concat({
          id: `${source.nodeId}-${source.handleId}-${targetId}`,
          source: source.nodeId,
          sourceHandle: source.handleId,
          target: targetId,
          targetHandle: "in",
        });
      }

      const canvasBounds = document
        .querySelector(".flow-canvas")
        .getBoundingClientRect();

      setPendingConnection({
        sourceNodeId: source.nodeId,
        sourceHandleId: source.handleId,
        position: {
          x: event.clientX - canvasBounds.left,
          y: event.clientY - canvasBounds.top,
        },
      });

      connectingNodeRef.current = null;
      return eds;
    });

    connectingNodeRef.current = null;
  };

  const performDelete = useCallback(
    (ids) => {
      const idSet = new Set(ids);

      setNodes((nds) => filterDeletedNodes(nds, idSet));

      setEdges((eds) => filterDeletedEdges(eds, idSet));

      setDeleteDialogOpen(false);
      setNodesToDelete([]);
    },
    [setEdges, setNodes],
  );

  const deleteNode = useCallback(
    (nodeId) => {
      const downstream = getDownstreamNodes(nodeId, edges);

      const allToDelete = [nodeId, ...downstream];

      if (downstream.length > 0) {
        setNodesToDelete(allToDelete);
        setDeleteDialogOpen(true);
        return;
      }

      performDelete([nodeId]);
    },
    [edges, performDelete],
  );

  const isQuestionValid = (nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return false;

    if (node.type !== "question") return true;

    const text = node.data?.questionText;
    return Boolean(text && text.trim().length > 0);
  };

  const enhancedNodes = useMemo(() => {
    const invalidNodeIds = validationResult?.invalidNodeIds;
    return nodes.map((n) =>
      enhanceSingleNode(n, invalidNodeIds, updateNodeData, deleteNode),
    );
  }, [deleteNode, nodes, updateNodeData, validationResult]);

  const handleSaveFlow = useCallback(async () => {
    const result = validateFlow(nodes, edges);
    setValidationResult(result);

    if (result.errors.length) {
      setConsoleOpen(true);
      return { success: false, validationErrors: result.errors };
    }

    const dsl = convertToNeuDSL(nodes, edges);
    const response = await saveWorkflowAPI(dsl);
    if (response?.id) {
      return { success: true, data: response };
    } else {
      showToastMessage({
        type: "error",
        message: response?.message || response?.msg || "",
      });
    }
  }, [edges, nodes, saveWorkflowAPI]);

  useEffect(() => {
    if (registerSave) {
      registerSave(handleSaveFlow);
    }
  }, [registerSave, handleSaveFlow]);

  return (
    <div
      className="flow-canvas"
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {pendingConnection && (
        <NodePicker
          position={pendingConnection.position}
          onSelect={createNodeFromPicker}
          onClose={() => setPendingConnection(null)}
        />
      )}
      <ReactFlow
        style={{ width: "100%", height: "100%" }}
        nodes={enhancedNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onEdgeClick={(_, edge) => setSelectedEdge(edge)}
        onConnect={onConnect}
        onConnectStart={onConnectStart}
        onConnectEnd={onConnectEnd}
        fitView
      >
        <Background variant="dots" gap={24} size={1} />
        <Controls />
        <MiniMap />
      </ReactFlow>
      <ValidationConsole
        open={consoleOpen}
        errors={validationResult?.errors || []}
        onClose={() => setConsoleOpen(false)}
        onSelectIssue={handleSelectIssue}
      />
      <CustomDialogBoxV2
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete Node and Downstream Branch"
        dialogSx={{
          zIndex: 2000,
        }}
      >
        <Box sx={{ minWidth: 400 }}>
          <Typography sx={{ mb: 2 }}>
            Deleting this node will also remove the following downstream nodes:
          </Typography>

          <Box
            sx={{
              maxHeight: 200,
              overflowY: "auto",
              border: "1px solid #eee",
              p: 1,
            }}
          >
            {nodesToDelete.map((id) => {
              const node = nodes.find((n) => n.id === id);
              return (
                <Typography key={id} variant="body2">
                  • {node?.type?.toUpperCase()} ({id})
                </Typography>
              );
            })}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 3,
            }}
          >
            <CustomButtonV2
              type="cancel"
              btnName="Cancel"
              handleOnClick={() => setDeleteDialogOpen(false)}
            />

            <CustomButtonV2
              type="submit"
              btnName="Delete All"
              handleOnClick={() => performDelete(nodesToDelete)}
            />
          </Box>
        </Box>
      </CustomDialogBoxV2>
    </div>
  );
}

FlowBuilder.propTypes = {
  registerSave: PropTypes.func,
  ruleSetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  registerLoad: PropTypes.func,
  workflowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
